// Document-type logic — the "pro forma" model.
//
// Our system is NOT a tax-compliant invoicing system. It produces internal
// PRO FORMA documents only — never an official "invoice". The OFFICIAL tax
// document is always issued elsewhere:
//   - Cash         → operator's own receipt book / till at the desk
//   - Bank         → the accountant issues the official invoice in their software
//   - Online       → (future) the payment processor issues the legal receipt
//
// So EVERYTHING is a "Pro Forma" today. A document only becomes a "Receipt"
// once a REAL payment-processor receipt is linked to it (inv.paymentReceiptRef),
// which will happen automatically when the Stripe/Viva payment integration is
// built. Until then there are no receipts — just pro formas, marked paid/unpaid
// so the operator knows which ones to enter into their official system.

export const ONLINE_PAY_METHODS = ['stripe', 'viva', 'paypal', 'online'];

export function isOnlinePaid(payMethod) {
  return ONLINE_PAY_METHODS.includes(payMethod);
}

/**
 * Returns 'draft' | 'receipt' | 'proforma'.
 * 'receipt' only when a real payment-processor receipt is linked
 * (inv.paymentReceiptRef) — i.e. after payment integration exists.
 * Today, every non-draft document is a pro forma.
 */
export function invoiceDocType(inv) {
  if (!inv) return 'proforma';
  if (inv.status === 'draft') return 'draft';
  if (inv.paymentReceiptRef && isOnlinePaid(inv.method || inv.payMethod)) return 'receipt';
  return 'proforma';
}

/** Big title shown on the document + modal header. No "invoice" wording. */
export function invoiceDocLabel(inv) {
  const t = invoiceDocType(inv);
  return t === 'draft'    ? 'DRAFT'
       : t === 'receipt'  ? 'PAYMENT RECEIPT'
       : 'PRO FORMA';
}

/** Short badge label for list rows. */
export function invoiceDocBadge(inv) {
  const t = invoiceDocType(inv);
  return t === 'draft'    ? 'Draft'
       : t === 'receipt'  ? 'Receipt'
       : 'Pro Forma';
}

/**
 * Display number with a prefix that matches the document type, so the number
 * never looks like an official tax-invoice number:
 *   pro forma → PF-0002
 *   receipt   → REC-0001
 *   draft     → DRAFT
 * The stored `num` keeps its raw sequence value (e.g. "INV-0002"); we only
 * change the prefix shown to the user. One shared counter feeds both types.
 */
export function invoiceDisplayNumber(inv) {
  if (!inv) return '';
  if (inv.status === 'draft' || !inv.num) return 'DRAFT';
  const tail = (String(inv.num).match(/(\d+)\s*$/) || [])[1] || String(inv.num);
  const t = invoiceDocType(inv);
  const prefix = t === 'receipt' ? 'REC-' : 'PF-';
  return prefix + tail;
}

/** Legal disclaimer footer text, varies by type. Empty for drafts. */
export function invoiceDocDisclaimer(inv) {
  const t = invoiceDocType(inv);
  if (t === 'receipt') {
    return 'Payment received online. An official receipt has been issued by the payment provider and emailed to the customer. This document is a record of that payment.';
  }
  if (t === 'proforma') {
    return 'This is a PRO FORMA document — not an official tax invoice. The official receipt is issued upon payment (at the desk for cash, or by our accounts department for bank transfers).';
  }
  return '';
}
